import { useState, useEffect } from 'react';
import { Brain, Clock, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronUp } from 'lucide-react';
import { api } from '../api/client';

const DECISION_CONFIG = {
  BUY:  { color: 'text-green-400', bg: 'bg-green-400/10', border: 'border-green-400/20', icon: TrendingUp  },
  SELL: { color: 'text-red-400',   bg: 'bg-red-400/10',   border: 'border-red-400/20',   icon: TrendingDown },
  HOLD: { color: 'text-slate-400', bg: 'bg-slate-400/10', border: 'border-slate-400/20', icon: Minus        },
};

function confidenceBar(confidence) {
  const pct = parseFloat(confidence) * 100 || 0;
  const color = pct >= 70 ? 'bg-green-400' : pct >= 50 ? 'bg-amber-400' : 'bg-slate-600';
  return (
    <div className="flex items-center gap-2">
      <div className="flex-1 h-1 bg-[#1E1E2E] rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] font-mono text-slate-400 w-8 text-right">{pct.toFixed(0)}%</span>
    </div>
  );
}

function DecisionRow({ decision }) {
  const [expanded, setExpanded] = useState(false);
  const cfg = DECISION_CONFIG[decision.decision] || DECISION_CONFIG.HOLD;
  const Icon = cfg.icon;

  const ts = new Date(decision.timestamp).toLocaleString('es-MX', {
    timeZone: 'America/Mexico_City',
    month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
  });

  return (
    <div className={`bg-[#13131A] border rounded transition-all duration-150 ${
      expanded ? 'border-amber-500/20' : 'border-[#1E1E2E]'
    }`}>
      {/* Row principal */}
      <div
        className="flex items-center gap-4 px-4 py-3 cursor-pointer hover:bg-white/[0.02]"
        onClick={() => setExpanded(!expanded)}
      >
        {/* Decisión */}
        <div className={`flex items-center gap-1.5 px-2 py-1 rounded border text-xs font-mono font-bold ${cfg.color} ${cfg.bg} ${cfg.border} w-16 justify-center shrink-0`}>
          <Icon size={11} />
          {decision.decision}
        </div>

        {/* Confianza */}
        <div className="w-28 shrink-0">
          {confidenceBar(decision.confidence)}
        </div>

        {/* Precio */}
        <span className="text-xs font-mono text-slate-300 w-20 shrink-0">
          {decision.price ? `$${parseFloat(decision.price).toFixed(2)}` : '—'}
        </span>

        {/* RSI M5 */}
        <span className="text-xs font-mono text-slate-500 w-16 shrink-0">
          RSI {decision.rsiM5 ? parseFloat(decision.rsiM5).toFixed(1) : '—'}
        </span>

        {/* Timestamp */}
        <span className="text-[11px] font-mono text-slate-600 flex-1 text-right">
          <Clock size={10} className="inline mr-1" />
          {ts}
        </span>

        {/* Expand */}
        <div className="text-slate-600 shrink-0">
          {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
        </div>
      </div>

      {/* Detalle expandido */}
      {expanded && (
        <div className="px-4 pb-4 border-t border-[#1E1E2E] pt-3 space-y-3">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Reason</p>
              <p className="text-xs font-mono text-slate-300">{decision.reasonCode || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Modelo</p>
              <p className="text-xs font-mono text-slate-300">{decision.modelUsed || '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Latencia</p>
              <p className="text-xs font-mono text-slate-300">{decision.latencyMs ? `${decision.latencyMs}ms` : '—'}</p>
            </div>
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Tokens</p>
              <p className="text-xs font-mono text-slate-300">
                {decision.tokensInput || 0} / {decision.tokensOutput || 0}
              </p>
            </div>
          </div>

          {decision.m5Context && (
            <div>
              <p className="text-[10px] text-slate-600 font-mono uppercase mb-1">Contexto M5</p>
              <p className="text-xs font-mono text-slate-400 bg-[#0A0A0F] rounded p-2 leading-relaxed">
                {decision.m5Context}
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Decisions() {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState(null);

  useEffect(() => {
    api.getDecisions({ limit: 50 })
      .then(r => setDecisions(r.data))
      .catch(() => setError('No se pudieron cargar las decisiones'))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex items-center justify-center h-full">
      <div className="flex items-center gap-3 text-amber-500 font-mono text-sm">
        <div className="w-2 h-2 rounded-full bg-amber-500 animate-ping" />
        Cargando decisiones...
      </div>
    </div>
  );

  const buys  = decisions.filter(d => d.decision === 'BUY').length;
  const sells = decisions.filter(d => d.decision === 'SELL').length;
  const holds = decisions.filter(d => d.decision === 'HOLD').length;

  return (
    <div className="p-8 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-100 tracking-tight">Decisiones</h1>
          <p className="text-xs text-slate-500 font-mono mt-1">Últimas {decisions.length} decisiones de Claude</p>
        </div>
        <div className="flex items-center gap-4">
          <span className="text-xs font-mono text-green-400">{buys} BUY</span>
          <span className="text-xs font-mono text-red-400">{sells} SELL</span>
          <span className="text-xs font-mono text-slate-500">{holds} HOLD</span>
        </div>
      </div>

      {error && (
        <div className="text-red-400 text-xs font-mono border border-red-400/20 bg-red-400/5 px-3 py-2 rounded">
          {error}
        </div>
      )}

      {decisions.length === 0 && !error && (
        <div className="flex items-center justify-center py-20">
          <div className="text-center">
            <Brain size={32} className="text-slate-700 mx-auto mb-3" />
            <p className="text-slate-600 font-mono text-sm">Sin decisiones registradas aún</p>
          </div>
        </div>
      )}

      {/* Lista */}
      <div className="space-y-2">
        {decisions.map(d => (
          <DecisionRow key={d.id} decision={d} />
        ))}
      </div>

    </div>
  );
}